import React, { useState } from 'react';
import type { Showcase, ShowcaseProperty, ShowcaseResource } from '../../types';
import { Phone, Mail, MapPin, Send, Loader2, CheckCircle } from 'lucide-react';
import { submitLead } from '../../services/ai-assistant';

interface ContactSectionProps {
  showcase: Showcase;
  property: ShowcaseProperty;
  resources: ShowcaseResource[];
  brandColour: string;
  darkMode?: boolean;
}

export default function ContactSection({ showcase, property, resources, brandColour, darkMode: dm }: ContactSectionProps) {
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [message, setMessage] = useState('');
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);

  const hasContact = showcase.contact_name || showcase.contact_email || showcase.contact_phone;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!email && !phone) return;

    setSending(true);
    try {
      await submitLead(showcase.id, { name, email, phone, message }, 'contact_form');
      setSent(true);
    } catch (err) {
      console.error('Failed to submit enquiry:', err);
    } finally {
      setSending(false);
    }
  };

  return (
    <section className={`py-16 transition-colors duration-300 ${dm ? 'bg-slate-900' : 'bg-white'}`}>
      <div className="max-w-5xl mx-auto px-8">
        <h2 className={`text-2xl font-bold mb-8 ${dm ? 'text-white' : 'text-slate-900'}`}>Get in Touch</h2>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-12">
          {hasContact && (
            <div className="space-y-6">
              {showcase.office_name && (
                <h3 className={`text-xl font-semibold ${dm ? 'text-white' : 'text-slate-900'}`}>{showcase.office_name}</h3>
              )}
              {showcase.contact_name && (
                <p className={`text-lg ${dm ? 'text-slate-300' : 'text-slate-700'}`}>{showcase.contact_name}</p>
              )}
              <div className="space-y-3">
                {showcase.contact_phone && (
                  <a href={`tel:${showcase.contact_phone}`} className={`flex items-center gap-3 transition-colors ${dm ? 'text-slate-300 hover:text-white' : 'text-slate-600 hover:text-slate-900'}`}>
                    <Phone className="w-5 h-5" style={{ color: brandColour }} />
                    {showcase.contact_phone}
                  </a>
                )}
                {showcase.contact_email && (
                  <a href={`mailto:${showcase.contact_email}`} className={`flex items-center gap-3 transition-colors ${dm ? 'text-slate-300 hover:text-white' : 'text-slate-600 hover:text-slate-900'}`}>
                    <Mail className="w-5 h-5" style={{ color: brandColour }} />
                    {showcase.contact_email}
                  </a>
                )}
                {showcase.office_address && (
                  <div className={`flex items-start gap-3 ${dm ? 'text-slate-300' : 'text-slate-600'}`}>
                    <MapPin className="w-5 h-5 mt-0.5" style={{ color: brandColour }} />
                    <span>{showcase.office_address}</span>
                  </div>
                )}
              </div>
              {showcase.logo_url && (
                <img src={showcase.logo_url} alt={showcase.office_name || ''} className="h-10 mt-4 opacity-70" />
              )}
            </div>
          )}

          <div>
            {sent ? (
              <div className="text-center py-10 animate-fade-in">
                <CheckCircle className="w-12 h-12 mx-auto mb-4" style={{ color: brandColour }} />
                <h3 className={`text-xl font-semibold mb-2 ${dm ? 'text-white' : 'text-slate-900'}`}>Enquiry Sent</h3>
                <p className={dm ? 'text-slate-400' : 'text-slate-500'}>We'll be in touch shortly.</p>
              </div>
            ) : (
              <form onSubmit={handleSubmit} className="space-y-4">
                {(['text', 'email', 'tel'] as const).map((type, i) => {
                  const vals = [name, email, phone];
                  const setters = [setName, setEmail, setPhone];
                  const placeholders = ['Your name', 'Email address', 'Phone number'];
                  const inputCls = `w-full px-4 py-3 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500 ${dm ? 'bg-slate-800 border-slate-700 text-white placeholder-slate-500' : 'bg-slate-50 border border-slate-200 text-slate-900 placeholder-slate-400'}`;
                  return (
                    <input key={type} type={type} value={vals[i]} onChange={(e) => setters[i](e.target.value)}
                      placeholder={placeholders[i]} required={type === 'email'} className={inputCls} />
                  );
                })}
                <textarea
                  value={message} onChange={(e) => setMessage(e.target.value)}
                  placeholder="Your message..." rows={4}
                  className={`w-full px-4 py-3 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500 resize-none ${dm ? 'bg-slate-800 border-slate-700 text-white placeholder-slate-500' : 'bg-slate-50 border border-slate-200 text-slate-900 placeholder-slate-400'}`}
                />
                <button
                  type="submit" disabled={sending}
                  className="w-full px-6 py-3 text-white rounded-xl font-medium flex items-center justify-center gap-2 transition-colors"
                  style={{ backgroundColor: brandColour }}
                >
                  {sending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
                  Send Enquiry
                </button>
              </form>
            )}
          </div>
        </div>
      </div>
    </section>
  );
}
